import { Check, KeyRound, Save } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitButtons } from '@unistocks/components/button/submit-buttons';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { toast } from '@unistocks/components/toast';
import { IdentityRoleDto } from '@unistocks/proxy/axios-client';
import {
  useUserGetAssignableRolesQuery,
  useUserGetRolesQuery,
  useUserUpdateRolesMutation
} from '@unistocks/proxy/axios-client/UserQuery';
import { t } from '@unistocks/shared/localization/index.ts';
import React, { useState } from 'react';
import { Checkbox, Label, Separator } from 'tamagui';

type UserRolesSheetProps = {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const UserRolesSheet: React.FC<UserRolesSheetProps> = ({ userId, open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const { data: currentRoles, isPending: isLoadingCurrent } = useUserGetRolesQuery(userId, {
    enabled: open
  });
  const { data: assignableRoles, isPending: isLoadingAssignable } = useUserGetAssignableRolesQuery({
    enabled: open
  });
  const { mutateAsync: updateRoles, isPending: isSaving } = useUserUpdateRolesMutation(userId, {
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: ['userGetRoles'] });
      onOpenChange(false);
    }
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [prevRoles, setPrevRoles] = useState(currentRoles);

  if (prevRoles !== currentRoles) {
    setPrevRoles(currentRoles);

    if (currentRoles?.items) {
      setSelectedRoles(currentRoles.items.map((r: IdentityRoleDto) => r.name!).filter(Boolean));
    }
  }

  const toggleRole = (roleName: string) => {
    setSelectedRoles(prev =>
      prev.includes(roleName) ? prev.filter(r => r !== roleName) : [...prev, roleName]
    );
  };

  const handleSave = () => {
    updateRoles({ roleNames: selectedRoles });
  };

  const isLoading = isLoadingCurrent || isLoadingAssignable;

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      headerTitle={t('AbpIdentity::Roles')}
      headerIcon={<KeyRound color={'$unistocks'} />}
    >
      {isLoading ? (
        <Loader mt={'$5'} />
      ) : (
        <Column gap={'$3'}>
          {assignableRoles?.items?.map((role: IdentityRoleDto) => (
            <Row key={role.id} alignItems="center">
              <Checkbox
                id={`role-${role.id}`}
                checked={selectedRoles.includes(role.name!)}
                onCheckedChange={() => toggleRole(role.name!)}
              >
                <Checkbox.Indicator>
                  <Check />
                </Checkbox.Indicator>
              </Checkbox>
              <Label htmlFor={`role-${role.id}`}>{role.name}</Label>
            </Row>
          ))}

          <Separator my={'$2'} />

          <SubmitButtons
            cancelButtonText={t('AbpUi::Cancel')}
            onCancel={() => onOpenChange(false)}
            submitButtonText={t('AbpUi::Save')}
            submitButtonIcon={Save}
            onSubmit={handleSave}
            isSubmitting={isSaving}
          />
        </Column>
      )}
    </FullScreenSheet>
  );
};

UserRolesSheet.displayName = 'UserRolesSheet';
